import type { ReploProject } from "schemas/generated/project";

import { useCallback } from "react";

import { toast } from "@replo/design-system/components/toast/Toast";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { ReploError } from "schemas/errors";

class ShopUpdateError extends ReploError {}
class InvalidTimezoneError extends ShopUpdateError {}

export type ShopSettings = Pick<ReploProject, "name" | "timeZone" | "currency">;

type ShopUpdateProps = {
  id: string;
  data: Partial<ShopSettings>;
  showSuccessToast?: boolean;
};

export function useUpdateShop() {
  const { mutateAsync: update, isPending } = projectRouter.update.useMutation({
    onSuccess: () => {
      void workspaceUtils.getUserWorkspacesList.invalidate();
      void projectUtils.listWithStats.invalidate();
      void projectUtils.listGroupedByWorkspaceForUser.invalidate();
      void projectUtils.getV2.invalidate();
    },
  });

  const updateShop = useCallback(
    async ({ id, data, showSuccessToast = true }: ShopUpdateProps) => {
      const { timeZone } = data;

      if (timeZone !== undefined) {
        const isValidTimezone =
          // TODO (Kurt, 2025-03-11, INS-421): We allow for null values to be
          // valid timezones here because in our database the timeZone field is
          // nullable. However, we have a ticket to make this non-nullable in the
          // future after backfilling the timeZone field with some default value.
          timeZone == null ||
          Intl.supportedValuesOf("timeZone").includes(timeZone) ||
          timeZone === "UTC";

        if (!isValidTimezone) {
          throw new InvalidTimezoneError({
            message: "Invalid timezone",
            additionalData: { timeZone },
          });
        }
      }

      const result = await update(
        {
          projectId: id,
          project: data,
        },
        {
          onSuccess: () => {
            if (showSuccessToast) {
              toast({ title: "Shop information updated successfully" });
            }
          },
        },
      );

      return result;
    },
    [update],
  );

  return {
    updateShop,
    isUpdating: isPending,
  };
}
